import type { ReactNode } from 'react';
import { ErrorPageCode } from '../../../lib/error-boundary/ErrorPageCode';
import { Icon } from '../../../lib/icon';
import { cn } from '../../../lib/utils';
import { Card, CardContent } from '../card';

type HeadingLevel = 'h2' | 'h3';

type EmptyStatusCardProps = {
  variant: 'empty';
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /** @default 'h3' */
  headingLevel?: HeadingLevel;
};

type ErrorStatusCardProps = {
  variant: 'error';
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  skeleton: ReactNode;
  skeletonIsCard?: boolean;
  error?: unknown;
  eventId?: string;
  copyLabel?: string;
  copiedLabel?: string;
  copyFailedLabel?: string;
  /** @default 'h3' */
  headingLevel?: HeadingLevel;
};

export type StatusCardProps = EmptyStatusCardProps | ErrorStatusCardProps;

const contentClasses = 'flex flex-col items-center justify-center gap-3 px-4 py-12 text-center';

export function StatusCard(props: StatusCardProps) {
  if (props.variant === 'empty') {
    const { icon, title, description, action, headingLevel: Heading = 'h3' } = props;
    return (
      <Card data-slot="status-card" data-variant="empty" className="overflow-hidden">
        <CardContent className={contentClasses}>
          {icon}
          <Heading className="font-semibold text-lg">{title}</Heading>
          {description != null ? <p className="max-w-sm text-muted-foreground text-sm">{description}</p> : null}
          {action}
        </CardContent>
      </Card>
    );
  }

  const {
    title,
    description,
    action,
    skeleton,
    skeletonIsCard = false,
    error,
    eventId,
    copyLabel,
    copiedLabel,
    copyFailedLabel,
    headingLevel: Heading = 'h3',
  } = props;
  const canRenderEventId = eventId && copyLabel && copiedLabel && copyFailedLabel;

  return (
    <Card data-slot="status-card" data-variant="error" className={cn('relative overflow-hidden p-0', skeletonIsCard && 'border-0')}>
      <div aria-hidden className={cn('pointer-events-none absolute inset-0 opacity-50', skeletonIsCard ? 'grid' : 'p-4')}>
        {skeleton}
      </div>
      <CardContent role="alert" className={cn('relative z-10', contentClasses)}>
        <Icon name="error" className="text-destructive" />
        <Heading className="font-semibold text-lg">{title}</Heading>
        {description != null ? <p className="max-w-sm text-muted-foreground text-sm">{description}</p> : null}
        {action}
        {canRenderEventId ? (
          <ErrorPageCode eventId={eventId} copyLabel={copyLabel} copiedLabel={copiedLabel} copyFailedLabel={copyFailedLabel} />
        ) : null}
        <DevErrorDetails error={error} />
      </CardContent>
    </Card>
  );
}

function DevErrorDetails({ error }: { error: unknown }) {
  if (!import.meta.env.DEV || error == null) {
    return null;
  }
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <details
      open
      className="mt-0 w-full rounded-lg border border-red-300 bg-red-50 p-4 text-left text-sm dark:border-red-800 dark:bg-red-950"
    >
      <summary className="cursor-pointer font-semibold text-red-700 dark:text-red-400">{message}</summary>
      {stack ? (
        <pre className="wrap-break-word mt-2 overflow-x-auto whitespace-pre-wrap text-red-700 text-xs dark:text-red-300">{stack}</pre>
      ) : null}
    </details>
  );
}
